"use client";

import React, { useState, useEffect } from "react";
import { Lock, ShieldCheck, FileText, Activity, CircleAlert, ScanEye, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  business_name: 'Assessment Watch',
  address: 'Investigation Bureau, New York, NY',
  phone: '(518) 555-0199',
  email: 'tips@assessmentwatch.com'
};
function SecureEngagementForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const minimalInputClass = "border-0 border-b border-white/20 rounded-none px-2 bg-white/10 focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-all duration-300 placeholder:text-white/30 h-12 text-white";
  if (isSubmitted && !submitError) {
    return <div data-section-id="2148" className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/20 blur-xl rounded-lg animate-pulse" />
          <ShieldCheck className="size-24 text-primary relative z-10" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold tracking-tight text-white font-mono uppercase">Tip Received</h3>
          <p className="text-white/70 max-w-md mx-auto">{successMessage}</p>
        </div>
        <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm flex items-center gap-3 text-sm text-primary font-mono">
          <Lock className="size-4" />
          <span>CASE ID: {Math.random().toString(36).substr(2, 9).toUpperCase()}</span>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-white/60 font-mono">Your Name (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="ANONYMOUS" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-white/60 font-mono">Secure Contact (Email)</FormLabel>
              <FormControl>
                <Input type="email" placeholder="EMAIL@EXAMPLE.COM" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-white/60 font-mono">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase tracking-widest text-white/60 font-mono">Subject Property Address</FormLabel>
              <FormControl>
                <Input placeholder="123 MAIN ST, NY" {...field} className={minimalInputClass} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="services" rules={{
      required: "Please select a tip category"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-white/60 font-mono mb-4 block">Tip Category</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["Tax Assessment", "Data Discrepancy", "Corporate Misconduct", "Other / General Tip"].map((service, i) => <div key={service} data-index={i} className="flex items-center space-x-3 p-3 border border-white/10 rounded-sm bg-white/5 hover:bg-white/10 transition-colors group">
                  <FormControl>
                    <input type="checkbox" className="appearance-none size-4 border border-primary/40 rounded-sm bg-transparent checked:bg-primary checked:border-primary focus:ring-1 focus:ring-primary/50 cursor-pointer relative after:content-[''] after:hidden checked:after:block after:absolute after:left-[5px] after:top-[1px] after:w-[5px] after:h-[10px] after:border-r-2 after:border-b-2 after:border-primary-foreground after:rotate-45 transition-all" checked={field.value?.includes(service)} onChange={e => {
              const checked = e.target.checked;
              const current = field.value || [];
              const updated = checked ? [...current, service] : current.filter((val: string) => val !== service);
              field.onChange(updated);
            }} />
                  </FormControl>
                  <span className="text-xs font-medium text-white/80 group-hover:text-primary transition-colors uppercase tracking-wide">{service}</span>
                </div>)}
            </div>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="details" rules={{
      required: "Investigation details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase tracking-widest text-white/60 font-mono">Investigation Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Provide specific evidence, addresses, or names related to the tax assessment issue you are reporting." {...field} className="min-h-[120px] bg-white/5 border-white/20 focus-visible:border-primary resize-none rounded-sm font-mono text-sm text-white" />
            </FormControl>
            <FormDescription className="text-[12px] text-tertiary font-mono flex items-center gap-1">
              <ShieldCheck className="size-3" /> WHISTLEBLOWER ANONYMITY PROTECTED
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="preferred_start_date" render={({
      field
    }) => <FormItem className="max-w-xs">
            <FormLabel className="text-[10px] uppercase tracking-widest text-white/60 font-mono">Incident Date</FormLabel>
            <FormControl>
              <Input type="date" {...field} className={minimalInputClass} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-sm flex items-start gap-3 animate-in slide-in-from-top-2">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive font-mono uppercase">Encryption Error</p>
            <p className="text-xs text-destructive/80">{submitError}</p>
          </div>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-bold tracking-[0.2em] uppercase bg-primary text-primary-foreground hover:bg-primary/90 border border-primary/50 rounded-sm transition-all hover:scale-[1.01] active:scale-[0.99]">
        {isSubmitting ? <>
            <Activity className="animate-spin mr-2 size-5" />
            ENCRYPTING...
          </> : <>
            <Lock className="mr-2 size-5" />
            ENCRYPT & SEND
          </>}
      </Button>
    </Form>;
}
export default function SecureEngagement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="submit-a-tip" className="bg-secondary text-white relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px]" />
        
        {}
        <div className="absolute -bottom-48 -right-48 size-[600px] rounded-lg border border-primary/10 animate-[ping_5s_ease-in-out_infinite] opacity-20" />
        <div className="absolute -bottom-48 -right-48 size-[800px] rounded-lg border border-primary/10 animate-[ping_7s_ease-in-out_infinite_1s] opacity-10" />
        
        {}
        <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-primary/5 blur-[120px] rounded-lg pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div ref={ref as React.RefObject<HTMLDivElement>} className={`lg:col-span-5 space-y-10 pt-8 ${isVisible ? 'animate-in slide-in-from-left-10 fade-in duration-700' : 'opacity-0'}`}>
            <div className="space-y-6">
              <Badge variant="outline" className="border-primary/40 text-primary bg-primary/5 px-3 py-1.5 rounded-sm uppercase tracking-widest text-[10px] font-mono">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-lg h-2 w-2 bg-primary"></span>
                </span>
                Whistleblower Line Active
              </Badge>
              
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight font-sans leading-tight">
                Submit a <span className="text-primary">Tip Securely</span>
              </h2>
              
              <p className="text-white/70 text-lg leading-relaxed border-l-2 border-primary/40 pl-6">
                Help us expose the broken New York property tax system. Our encrypted portal ensures your identity and evidence remain confidential throughout our investigative process.
              </p>
            </div>

            <div className="space-y-4">
              {[{
              icon: ShieldCheck,
              title: "Anonymity Guaranteed",
              desc: "All submissions are processed through high-level encryption standards to protect your identity."
            }, {
              icon: ScanEye,
              title: "Expert Verification",
              desc: "Our investigative journalists verify every lead with professional precision and legal oversight."
            }, {
              icon: Activity,
              title: "Public Impact",
              desc: "Your information fuels the reports that hold the tax assessment system accountable."
            }].map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 p-4 rounded-sm border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-colors group">
                  <div className="p-2 bg-primary/20 rounded-sm text-primary shrink-0 group-hover:text-primary-foreground group-hover:bg-primary transition-colors">
                    <item.icon className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white font-mono text-sm uppercase tracking-wide">{item.title}</h4>
                    <p className="text-sm text-white/60 mt-1">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-8 border-t border-white/10">
              
              <div className="flex flex-col sm:flex-row gap-6 text-sm font-medium font-mono">
                
                
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 ${isVisible ? 'animate-in slide-in-from-right-10 fade-in duration-700 delay-200' : 'opacity-0'}`}>
            <Card className="bg-secondary/50 backdrop-blur-xl border-white/10 shadow-2xl relative overflow-hidden rounded-sm">
              {}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-70" />
              <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <FileText className="size-32 -rotate-12 text-white" />
              </div>

              <CardHeader className="border-b border-white/10 pb-6 relative z-10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-mono uppercase tracking-widest flex items-center gap-2 text-primary">
                      <Lock className="size-4" />
                      Secure Submission Portal
                    </CardTitle>
                    <CardDescription className="mt-1 font-mono text-[10px] text-white/40">
                      REF: AW-TIP-2024-SECURE
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="hidden sm:flex border-primary/30 text-primary bg-primary/10 font-mono text-[10px] tracking-widest rounded-sm">
                    CONFIDENTIAL
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-8 relative z-10">
                <FormProvider formKey="tip_submission_form_home" formId="tip_submission_form_home" sectionName="secure-engagement" defaultValues={{
                name: "",
                email: "",
                phone: "",
                organization: "",
                services: [],
                details: "",
                preferred_start_date: ""
              }} validationRules={{
                name: {
                  required: false
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: false
                },
                services: {
                  required: true
                },
                details: {
                  required: true
                }
              }} submitText="ENCRYPT & SEND" successMessage="Your tip has been securely encrypted and sent to our investigative team. We protect our sources with absolute confidentiality.">
                  <SecureEngagementForm />
                </FormProvider>
              </CardContent>
              
              
            </Card>
          </div>

        </div>
      </div>
    </section>;
}